/**
 * Blank module
 */

import { getMutationsForScope, getActionsForScope } from './../utils';
import api from './../../api';
import {
  errors, LANDING_URL, DEV, SUBMISSION_CATEGORIES
} from '../../config';

const mutation = getMutationsForScope('submission');
const action = getActionsForScope('submission');

/**
 * Build the form data for submission
 *
 * @param {*} {
 *     image,
 *     video,
 *     name,
 *     ratio,
 *     thumbnail,
 *     category,
 *     consentName,
 *     mimeType,
 *     duration,
 *   }
 */
function getSubmissionPayload(
  {
    image,
    video,
    name,
    aspectRatio,
    thumbnail,
    category,
    consentName,
    mimetype,
    duration,
  },
  user
) {
  if (!category || (!image && !video)) {
    throw new Error(errors.SUBMISSION_NO);
  }

  // make submission payload
  const formData = new FormData();

  if (category === SUBMISSION_CATEGORIES.image) {
    formData.append('image', image, name);
  } else {
    formData.append('video', video, name);
    formData.append('duration', duration);
  }
  if (thumbnail) {
    formData.append('thumbnail', thumbnail, 'thumbnail.png');
  }

  formData.append('return_path', LANDING_URL);
  formData.append('category', category);
  formData.append('consentName', consentName);
  formData.append('aspect_ratio', aspectRatio);
  formData.append('mimetype', mimetype);
  formData.append('access_token', user.access_token);
  formData.append('id_token', user.id_token);

  return formData;
}

const moduleState = {
  complete: false, // if the submission is complete
  image: null, // maybe this is where we put the image blob? it can go in storage so that we like, can use it elsewhere
  video: null, // video upload url?
  lastCall: null, // the last call that was made for cancelling
  urls: null, // the share urls, probably needs a better name
  submissionId: null, // the id of the successful submission
  error: null, // the last error object, not actually useful when using vue developer tools ¯\_(ツ)_/¯
  progress: 0, // fraction of the submission uploaded
  status: null, // if the submission is pending, etc
  submissionSharePath: window === window.parent ? '' : process.env.VUE_APP_PARENT_PATH, // path prepended to share urls, basically the path we're served from in the parent
};

const moduleGetters = {

};

const mutations = {
  /**
   * Set the root path of the share links
   *
   * @param {*} state
   * @param {*} { parentPath }
   */
  [mutation.SUBMISSION_SET_SHARE_PATH](state, { parentPath }) {
    state.submissionSharePath = parentPath || state.submissionSharePath;
  },

  /**
   * Set submission as complete
   *
   * @param {*} state
   * @param {*} payload
   * @param {Boolean} payload.complete
   */
  [mutation.SUBMISSION_SET_COMPLETE](state, { complete }) {
    state.complete = complete;
  },

  /**
   * Set share urls
   *
   * @param {*} state
   * @param {*} { url }
   */
  [mutation.SUBMISSION_SET_SHARE_URL](state, { urls }) {
    // make share url
    const rootPath = LANDING_URL.replace('[[path]]', state.submissionSharePath);
    state.urls = `${rootPath}${urls}`;
  },

  /**
   * Set submission status
   *
   * @param {*} state
   * @param {*} { url }
   */
  [mutation.SUBMISSION_SET_STATUS](state, { status }) {
    state.status = status;
  },

  /**
   * Set last call reference
   *
   * @param {*} state
   * @param {*} payload
   * @param {GrowBaseAPI~Call} payload.call
   */
  [mutation.SUBMISSION_SET_LAST_CALL](state, { call }) {
    state.call = call;
  },

  /**
   * Set submission id
   *
   * @param {*} state
   * @param {*} payload
   * @param {String} payload.submissionId
   */
  [mutation.SUBMISSION_SET_ID](state, { submissionId }) {
    state.submissionId = submissionId;
  },

  /**
   * Set error
   *
   * @param {*} state
   * @param {*} payload
   * @param {Error} payload.error { error }
   */
  [mutation.SUBMISSION_SET_ERROR](state, { error }) {
    state.error = error;
  },

  /**
   * Set upload progress (out of 1)
   *
   * @param {*} state
   * @param {*} payload
   * @param {Number} payload.value Float progress
   */
  [mutation.SUBMISSION_SET_PROGRESS](state, { value }) {
    state.progress = value;
  },

  /**
   * Save thumbnail so that we can display later
   *
   * @param {*} state
   * @param {*} { thumbnail }
   */
  [mutation.SUBMISSION_SET_THUMBNAIL](state, { thumbnail }) {
    if (thumbnail) {
      if (typeof thumbnail === 'string') {
        state.thumbnail = thumbnail;
        return;
      }

      state.thumbnail = URL.createObjectURL(thumbnail);
      return;
    }

    state.thumbnail = null;
  },

  /**
   * clear thumbnail for memory purposes
   *
   * @param {*} state
   * @param {*} { thumbnail }
   */
  [mutation.SUBMISSION_DELETE_THUMBNAIL](state) {
    URL.revokeObjectURL(state.thumbnail);
    state.thumbnail = null;
  }
};

const actions = {
  /**
   * Make submission on behalf of the current user
   *
   * @param {*} context
   * @param {*} payload
   * @param {*} payload.submission the video or image
   * @param {*} payload.category the type of entry, video or image
   * @param {*} payload.name filename
   * @param {*} payload.dev turn off login checking in dev
   * @returns {Promise.<null>} resolves when submission is complete
   */
  async [action.SUBMISSION_MAKE_SUBMISSION](
    {
      commit,
      dispatch,
      state: {
        lastCall,
      },
      rootState: {
        user,
      },
      rootGetters,
    },
    payload = {}
  ) {
    // check existing submission
    if (rootGetters['user/madeSubmission']) {
      // can't make a second submission
      // UI should navigate them to the success page
      throw new Error(errors.SUBMISSION_LIMIT);
    }


    if (lastCall) {
      // cancel last call

      await dispatch(action.SUBMISSION_CANCEL);
    }

    // not enforcing this in dev because its annoying me - AH
    if (!payload.dev || DEV) {
      // can't make a submission if they aren't logged in.
      if (!user.loggedIn) {
        throw new Error(errors.USER_LOGIN_NO);
      }
    }

    const formData = getSubmissionPayload(payload, user.user);

    // save the thumbnail so that we can display it
    commit(mutation.SUBMISSION_SET_THUMBNAIL, { thumbnail: payload.thumbnail });

    // make a new call
    const call = api.makeSubmission(
      formData,
      (event) => {
        dispatch(action.SUBMISSION_HANDLE_PROGRESS, { event });
      }
    );

    // save reference for cancelling
    commit(mutation.SUBMISSION_SET_LAST_CALL, { call });

    return call.promise.then((response) => {
      // get the share url {
      if (!response.success && !response.data) {
        const error = new Error(errors.SUBMISSION_SERVER_ERROR);
        error.response = response;
        throw error;
      }

      // clear errors
      // handle sucess
      const { data } = response;

      dispatch(action.SUBMISSION_CLEAR_ERROR);
      dispatch(action.SUBMISSION_SUCCESS, { data: data.submission });
    })
      .catch((error) => {
        error.message = errors.SUBMISSION_SERVER_ERROR;
        dispatch(action.SUBMISSION_HANDLE_ERROR, {
          error,
        });
      });
  },

  /**
   * Update state when submission is successful
   *
   * @param {*} context
   * @param {*} payload
   * @param {*} payload.data the submission info
   */
  [action.SUBMISSION_SUCCESS]({
    state,
    commit,
  }, {
    data: submissionData,
  }) {
    // save shares
    commit(mutation.SUBMISSION_SET_SHARE_URL, {
      urls: submissionData.id,
    });

    // save id
    commit(mutation.SUBMISSION_SET_ID, {
      submissionId: submissionData.id,
    });

    if (!state.thumbnail && submissionData.thumbnail) {
      // save the thumbnail so that we can display it
      commit(mutation.SUBMISSION_SET_THUMBNAIL, { thumbnail: submissionData.thumbnail.url });
    }

    // flag complete
    commit(mutation.SUBMISSION_SET_COMPLETE, {
      complete: true,
    });

    // flag complete
    commit(mutation.SUBMISSION_SET_PROGRESS, {
      value: 1,
    });

    // clear call
    commit(mutation.SUBMISSION_SET_LAST_CALL, {
      call: null,
    });

    // save status
    commit(mutation.SUBMISSION_SET_STATUS, {
      status: submissionData.status,
    });
  },

  /**
   * Clear the error
   *
   * @param {*} context
   */
  [action.SUBMISSION_CLEAR_ERROR]({
    commit,
  }) {
    commit(mutation.SUBMISSION_SET_ERROR, { error: null });
  },

  /**
   * Cancel the existing call.
   *
   * @param {*} context cancel existing call so we can make another one
   * @returns {Promise.<null>} resolves when call is over and gone
   */
  [action.SUBMISSION_CANCEL]({
    commit,
    state: {
      lastCall,
    },
  }) {
    if (lastCall) {
      lastCall.abort();
    }

    commit(mutation.SUBMISSION_SET_LAST_CALL, { call: null });

    return null;
  },

  /**
   * Clear submission
   *
   * @param {*} context
   * @returns {Promise.<Null>} resolves when the submission is reset
   */
  [action.SUBMISSION_CLEAR]({
    commit,
    dispatch,
  }) {
    // save shares
    commit(mutation.SUBMISSION_SET_SHARE_URL, {
      urls: null,
    });

    // save id
    commit(mutation.SUBMISSION_SET_ID, {
      submissionId: null,
    });

    // flag incomplete
    commit(mutation.SUBMISSION_SET_COMPLETE, {
      complete: false,
    });

    // flag complete
    commit(mutation.SUBMISSION_SET_PROGRESS, {
      value: 0,
    });

    // clear thumbnail
    commit(mutation.SUBMISSION_DELETE_THUMBNAIL);

    // clear status
    commit(mutation.SUBMISSION_SET_STATUS, {
      status: null,
    });

    return Promise.all([
      dispatch(action.SUBMISSION_CLEAR_ERROR),
      dispatch(action.SUBMISSION_CANCEL),
    ]);
  },

  /**
   * Clear the submission store on error
   *
   * @param {*} context
   * @param {*} payload
   * @param {Error} payload.error
   * @param {Error} [payload.error.response] response from server
   */
  [action.SUBMISSION_HANDLE_ERROR](
    {
      commit,
    },
    {
      error,
    },
  ) {
    // clear submission in the event of an error
    // save shares
    commit(mutation.SUBMISSION_SET_SHARE_URL, {
      urls: null,
    });

    // save id
    commit(mutation.SUBMISSION_SET_ID, {
      submissionId: null,
    });

    // flag incomplete
    commit(mutation.SUBMISSION_SET_COMPLETE, {
      complete: false,
    });

    // clear call
    commit(mutation.SUBMISSION_SET_LAST_CALL, {
      call: null,
    });

    // clear call
    commit(mutation.SUBMISSION_SET_PROGRESS, {
      value: 0,
    });

    // clear status
    commit(mutation.SUBMISSION_SET_STATUS, {
      status: null,
    });

    // clear thumbnail
    commit(mutation.SUBMISSION_DELETE_THUMBNAIL);

    // save error
    commit(mutation.SUBMISSION_SET_ERROR, { error });

    throw error;
  },

  /**
   * Update progess handler
   *
   * @param {*} context
   * @param {ProgressEvent} payload
   */
  [action.SUBMISSION_HANDLE_PROGRESS](
    {
      commit,
    },
    {
      loaded,
      total,
      lengthComputable,
    }
  ) {
    let value;
    if (!lengthComputable) {
      value = 0;
    } else {
      value = total ? loaded / total : 0;
    }

    commit(mutation.SUBMISSION_SET_PROGRESS, {
      value
    });
  },

  /**
   * Check if the logged in user has already made a submission, if so they go direct to the success page
   *
   * @param {*} context
   * @returns {Promise.<Boolean>} true if the user has already submitted
   */
  async [action.SUBMISSION_GET_USER_SUBMISSION](
    {
      dispatch,
      commit,
      state: {
        lastCall,
      },
      rootState: {
        user: {
          loggedIn,
          user: userToken,
        },
      },
      rootGetters,
    },
  ) {
    if (!loggedIn) {
      // log in and then try again

      return dispatch(action.USER_LOG_IN, null, { root: true })
        .then((loginSuccessful) => {
          if (!loginSuccessful) {
            throw new Error(errors.USER_LOGIN_NO);
          }
          return dispatch(action.SUBMISSION_GET_USER_SUBMISSION);
        });
    }

    console.log('Past login', userToken);

    // user has already made submission, we have it in store
    if (rootGetters['user/madeSubmission']) {
      return true;
    }

    if (lastCall) {
      // cancel last call

      await dispatch(action.SUBMISSION_CANCEL);
    }

    const call = api.getUserSubmission(userToken);

    // save reference for cancelling
    commit(mutation.SUBMISSION_SET_LAST_CALL, { call });

    return call.promise.then((response) => {
      // get the share url {
      if (!response.success && !response.data) {
        const error = new Error(errors.SUBMISSION_SERVER_ERROR);
        error.response = response;
        throw error;
      }

      // clear errors
      // handle sucess
      const { data } = response;

      dispatch(action.SUBMISSION_CLEAR_ERROR);

      if (data.submission) {
        // submission found
        dispatch(action.SUBMISSION_SUCCESS, { data: data.submission });
        return true;
      }

      // no submission found
      return false;
    })
      .catch((error) => {
        error.message = errors.SUBMISSION_SERVER_ERROR;
        dispatch(action.SUBMISSION_HANDLE_ERROR, {
          error,
        });
      });
  },
};

export default {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
